/**
 * 共享查询钩子：查询键与 queryFn 集中一处，保证各页面缓存一致（避免同资源多份缓存）。
 * 所有错误统一由 App.tsx 里的 QueryCache.onError 弹 Toast，页面再渲染 ErrorState 做整块提示。
 */
import { useQuery, type Query } from '@tanstack/react-query';
import { advisorFeedbackApi } from '../api/advisor-feedback.js';
import type { TeamFeedbackDto } from '../api/types.js';
import { advisorsApi } from '../api/advisors.js';
import { candidatesApi } from '../api/candidates.js';
import { demoApi } from '../api/demo.js';
import { eventsApi } from '../api/events.js';
import { gapApi } from '../api/gap.js';
import { incubatorApi } from '../api/incubator.js';
import { meApi } from '../api/me.js';
import { tasksApi } from '../api/tasks.js';
import { teamsApi } from '../api/teams.js';
import { workbenchApi } from '../api/workbench.js';
import { qk } from '../lib/queryKeys.js';

export function useMeQuery() {
  return useQuery({ queryKey: qk.me(), queryFn: () => meApi.get() });
}

export function useMyTeamsQuery() {
  return useQuery({ queryKey: qk.myTeams(), queryFn: () => teamsApi.listMine() });
}

export function useMyTasksQuery() {
  return useQuery({ queryKey: qk.myTasks(), queryFn: () => tasksApi.listMine() });
}

export function useCandidatesQuery(eventCode?: string) {
  return useQuery({ queryKey: qk.candidates(eventCode), queryFn: () => candidatesApi.list(eventCode) });
}

/** 正在招募的队伍卡片（T72）：当前无页面直接消费（招募卡已由组队大厅承载），保留以备 organizer 视图复用 */
export function useRecruitingQuery(eventCode?: string) {
  return useQuery({ queryKey: qk.recruiting(eventCode), queryFn: () => teamsApi.listRecruiting(eventCode) });
}

/** 指导老师列表（T77） */
export function useAdvisorsQuery() {
  return useQuery({ queryKey: qk.advisors(), queryFn: () => advisorsApi.list() });
}

/** 队伍技能缺口（扩展①；仅成员/admin 可见，后端会挡） */
export function useGapQuery(teamId: string | null) {
  const enabled = teamId !== null && teamId.length > 0;
  return useQuery({
    queryKey: qk.gap(teamId ?? 'none'),
    queryFn: () => gapApi.get(teamId ?? ''),
    enabled,
  });
}

/** 赛事需求集（扩展①；编辑前展示用） */
export function useEventRequirementsQuery(eventCode: string | null) {
  const enabled = eventCode !== null && eventCode.length > 0;
  return useQuery({
    queryKey: qk.eventRequirements(eventCode ?? 'none'),
    queryFn: () => gapApi.getRequirements(eventCode ?? ''),
    enabled,
  });
}

/** 组队大厅卡片（扩展②） */
export function useHallQuery(eventCode?: string) {
  return useQuery({ queryKey: qk.hall(eventCode), queryFn: () => teamsApi.listHall(eventCode) });
}

/** 队伍文件中心（扩展③；仅成员/admin 可见，后端会挡） */
export function useTeamFilesQuery(teamId: string | null) {
  const enabled = teamId !== null && teamId.length > 0;
  return useQuery({
    queryKey: qk.teamFiles(teamId ?? 'none'),
    queryFn: () => workbenchApi.listFiles(teamId ?? ''),
    enabled,
  });
}

/** 团队决策日志（扩展③） */
export function useTeamDecisionsQuery(teamId: string | null) {
  const enabled = teamId !== null && teamId.length > 0;
  return useQuery({
    queryKey: qk.teamDecisions(teamId ?? 'none'),
    queryFn: () => workbenchApi.listDecisions(teamId ?? ''),
    enabled,
  });
}

/** 项目备赛进度（扩展③；成队后才有 projectId） */
export function useProjectProgressQuery(projectId: string | null) {
  const enabled = projectId !== null && projectId.length > 0;
  return useQuery({
    queryKey: qk.projectProgress(projectId ?? 'none'),
    queryFn: () => workbenchApi.progress(projectId ?? ''),
    enabled,
  });
}

/** 项目孵化器方向室（扩展⑦：原话 + 聚类候选 + 当前方向） */
export function useTeamIdeasQuery(teamId: string | null) {
  const enabled = teamId !== null && teamId.length > 0;
  return useQuery({
    queryKey: qk.teamIdeas(teamId ?? 'none'),
    queryFn: () => incubatorApi.view(teamId ?? ''),
    enabled,
  });
}

/** 导师反馈列表（扩展⑥；仅成员/admin 可见）。refetchInterval 传函数可实现「有待生成项时才轮询」。 */
export function useTeamFeedbackQuery(
  teamId: string | null,
  refetchInterval?: number | ((query: Query<{ feedback: TeamFeedbackDto[] }>) => number | false),
) {
  const enabled = teamId !== null && teamId.length > 0;
  return useQuery({
    queryKey: qk.teamFeedback(teamId ?? 'none'),
    queryFn: () => advisorFeedbackApi.list(teamId ?? ''),
    enabled,
    refetchInterval,
  });
}

export function useProjectTasksQuery(projectId: string | null) {
  const enabled = projectId !== null && projectId.length > 0;
  return useQuery({
    queryKey: qk.projectTasks(projectId ?? 'none'),
    queryFn: () => tasksApi.listByProject(projectId ?? ''),
    enabled,
  });
}

/** 赛事列表（Events 页）；可传 {scope, school} 过滤（v0.3 竞赛资讯分类） */
export function useEventsQuery(filter?: { scope?: 'common' | 'exclusive'; school?: string }) {
  return useQuery({
    queryKey: qk.events(filter),
    queryFn: () => eventsApi.list(filter),
  });
}

/** 赛事时间轴（EventDetail 页：里程碑含 daysLeft + 动态） */
export function useEventTimelineQuery(code: string | null) {
  const enabled = code !== null && code.length > 0;
  return useQuery({
    queryKey: qk.eventTimeline(code ?? 'none'),
    queryFn: () => eventsApi.timeline(code ?? ''),
    enabled,
  });
}

/**
 * 演示数据条数（Layout 常显角标 + 设置页区块共用）。
 *
 * 「静默降级」的两个开关缺一不可：
 * - `meta.silent` → App.tsx 的 QueryCache.onError 不会为它弹全局错误 Toast
 *   （角标是装饰性信息，后端没上线时不该每次都糊用户一脸报错）；
 * - `retry: false` → 后端未就绪时不反复重试。
 * 调用方拿不到数据就自行降级成「我的数据」，页面不渲染任何错误块。
 */
export function useDemoStatusQuery(enabled = true) {
  return useQuery({
    queryKey: qk.demoStatus(),
    queryFn: () => demoApi.status(),
    enabled,
    retry: false,
    staleTime: 30_000,
    meta: { silent: true },
  });
}